/**
 * "All records": every record screen and every service operation of the CRM backend, generated from crmCatalog.ts.
 * Not run against the live API. The fields of each screen come from GET <path>/schema at the moment it is opened.
 *
 * What it covers: list, search, sort, pages, create, edit (If-Match), delete, CSV export, the record actions (with their bodies),
 * the service operations (route values, query, body, file upload, file download) and live refresh over SignalR.
 * What it cannot know: who may do what. A button the person has no permission for answers 403 and the message is shown.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Crmexplorer.module.css";
import { ENTITIES, MODULES, OPERATIONS, type CatalogCall, type CatalogEntity, type CatalogField } from "../../types/Crmcatalog";
import { coerce, fillRoute, type ApiRecord, type CrmApi, type Schema, type SchemaField } from "./Api/Crmapi";
import { useCrmBackend } from "./Crmbackend";

const PAGE_SIZE = 25;
const label = (s: string) => s.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[-_]/g, " ").replace(/^./, (c) => c.toUpperCase());
const modName = (k: string) => MODULES[k] ?? label(k);

const fmt = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return /^\d{4}-\d\d-\d\dT/.test(s) ? s.slice(0, 16).replace("T", " ") : s;
};

const errText = (e: unknown): string => {
  const o = (typeof e === "object" && e !== null ? e : {}) as { status?: unknown; message?: unknown; fields?: unknown };
  const status = typeof o.status === "number" ? o.status : 0;
  const msg = typeof o.message === "string" ? o.message : String(e);
  if (!status) return msg;
  const fields = o.fields && typeof o.fields === "object" ? Object.entries(o.fields as Record<string, unknown>).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`) : [];
  const base = status === 403 ? "You do not have permission for this." : status === 401 ? "Sign in again: the token was not accepted." : status === 409 || status === 412 ? "Someone else changed this record. Reload it and try again." : msg;
  return fields.length ? `${base} (${fields.join("; ")})` : base;
};

/* ------------------------------------------------------------------ small pieces */

/** Grey placeholder shapes shown while a screen loads: summary tiles (optional) and table rows. */
export const Skeleton: React.FC<{ rows?: number; cols?: number; tiles?: boolean }> = ({ rows = 8, cols = 5, tiles = false }) => (
  <div aria-busy="true" aria-label="Loading">
    {tiles ? (
      <div className={styles.cxSkelTiles}>
        {[0, 1, 2, 3].map((i) => <div key={i} className={styles.cxSkelTile}><span className={styles.cxSkelBar} style={{ width: "50%" }} /><span className={styles.cxSkelBar} style={{ height: 22, width: "70%" }} /></div>)}
      </div>
    ) : null}
    <div className={styles.cxScroll}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className={styles.cxSkelRow}>
          {Array.from({ length: cols }, (_, c) => <span key={c} className={styles.cxSkelBar} style={{ width: `${55 + ((r * 7 + c * 13) % 40)}%` }} />)}
        </div>
      ))}
    </div>
  </div>
);

const FieldInput: React.FC<{ f: { name: string; type: string; required?: boolean }; value: string; onChange: (v: string) => void }> = ({ f, value, onChange }) => {
  const id = `cx-${f.name}`;
  let control: React.ReactNode;
  if (f.type === "boolean") {
    control = (
      <select id={id} className={styles.cxInput} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">(not set)</option><option value="true">Yes</option><option value="false">No</option>
      </select>
    );
  } else if (f.type === "json") {
    control = <textarea id={id} className={`${styles.cxInput} ${styles.cxMono}`} rows={3} value={value} placeholder="JSON" onChange={(e) => onChange(e.target.value)} />;
  } else {
    const type = f.type === "integer" || f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "datetime" ? "datetime-local" : "text";
    control = <input id={id} className={styles.cxInput} type={type} step={f.type === "number" ? "any" : undefined} value={value} placeholder={f.type === "guid" ? "id (GUID)" : ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className={styles.cxField}>
      <label htmlFor={id}>{label(f.name)}{f.required ? <span className={styles.cxReq}> *</span> : null}<span className={styles.cxType}>{f.type}</span></label>
      {control}
    </div>
  );
};

const toInput = (type: string, v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (type === "json") return typeof v === "string" ? v : JSON.stringify(v, null, 2);
  if (type === "datetime") return String(v).slice(0, 16);
  if (type === "date") return String(v).slice(0, 10);
  return String(v);
};

/** Builds the JSON body from the form strings. Throws a readable error for bad numbers or bad JSON. */
function buildBody(fields: { name: string; type: string; required?: boolean }[], vals: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = vals[f.name] ?? "";
    if (raw.trim() === "") { if (f.required) throw new Error(`${label(f.name)} is required.`); continue; }
    try {
      const v = coerce(f.type, raw);
      if (typeof v === "number" && Number.isNaN(v)) throw new Error("not a number");
      out[f.name] = v;
    } catch { throw new Error(`${label(f.name)} is not a valid ${f.type}.`); }
  }
  return out;
}

const ResultView: React.FC<{ data: unknown }> = ({ data }) => {
  const list = Array.isArray(data) ? data : data && typeof data === "object" && Array.isArray((data as any).items) ? (data as any).items : null;
  const objs = list && list.length > 0 && list.every((x: unknown) => x && typeof x === "object" && !Array.isArray(x)) ? (list as Record<string, unknown>[]) : null;
  const cols = objs ? Array.from(new Set(objs.flatMap((o) => Object.keys(o)))).slice(0, 8) : [];
  return (
    <div className={styles.cxResult}>
      {objs ? (
        <div className={styles.cxScroll}>
          <table className={styles.cxTable}>
            <thead><tr>{cols.map((c) => <th key={c}>{label(c)}</th>)}</tr></thead>
            <tbody>{objs.slice(0, 200).map((o, i) => <tr key={i}>{cols.map((c) => <td key={c}>{fmt(o[c])}</td>)}</tr>)}</tbody>
          </table>
        </div>
      ) : null}
      <details open={!objs}>
        <summary>Raw response</summary>
        <pre className={styles.cxPre}>{data === null || data === undefined ? "(empty response)" : JSON.stringify(data, null, 2)}</pre>
      </details>
    </div>
  );
};

/* ------------------------------------------------------------------ one call (record action or service operation) */

const CallForm: React.FC<{ api: CrmApi; call: CatalogCall; basePath: string; recordId?: string; onDone?: () => void }> = ({ api, call, basePath, recordId, onDone }) => {
  const [route, setRoute] = useState<Record<string, string>>({});
  const [query, setQuery] = useState<Record<string, string>>({});
  const [body, setBody] = useState<Record<string, string>>({});
  const [rawBody, setRawBody] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ data: unknown } | null>(null);

  const hasBodyMethod = call.method === "POST" || call.method === "PUT" || call.method === "PATCH";
  const unknownBody = hasBodyMethod && call.body.length === 0 && !call.upload;

  const run = async () => {
    setErr(null); setResult(null); setBusy(true);
    try {
      const full = recordId ? `${basePath}/${recordId}/${call.path}` : call.path;
      const { path, missing } = fillRoute(full, route);
      if (missing.length) throw new Error(`Fill in: ${missing.map(label).join(", ")}.`);
      const q: Record<string, string> = {};
      for (const f of call.query) {
        const raw = query[f.name] ?? "";
        if (raw.trim() === "") { if (f.required) throw new Error(`${label(f.name)} is required.`); continue; }
        const v = coerce(f.type, raw);
        q[f.name] = String(v);
      }
      if (call.download) {
        await api.download(call.method, path, { query: q }, call.label);
        setResult({ data: { downloaded: true } });
        return;
      }
      if (call.upload) {
        if (!file) throw new Error("Choose a file.");
        const form = new FormData();
        form.append(call.fileField || "file", file);
        for (const f of call.body) { const raw = body[f.name] ?? ""; if (raw.trim() !== "") form.append(f.name, raw); }
        setResult({ data: await api.json(call.method, path, { query: q, form }) });
      } else if (hasBodyMethod) {
        let payload: unknown = undefined;
        if (call.body.length > 0) payload = buildBody(call.body, body);
        else if (rawBody.trim() !== "") { try { payload = JSON.parse(rawBody); } catch { throw new Error("The body is not valid JSON."); } }
        else if (!call.bodyOptional) payload = {};
        setResult({ data: await api.json(call.method, path, { query: q, body: payload }) });
      } else {
        setResult({ data: await api.json(call.method, path, { query: q }) });
      }
      if (call.method !== "GET") onDone?.();
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const group = (title: string, fields: CatalogField[], vals: Record<string, string>, set: (v: Record<string, string>) => void) =>
    fields.length === 0 ? null : (
      <div className={styles.cxGroup}>
        <div className={styles.cxGroupT}>{title}</div>
        <div className={styles.cxGrid}>{fields.map((f) => <FieldInput key={f.name} f={f} value={vals[f.name] ?? ""} onChange={(v) => set({ ...vals, [f.name]: v })} />)}</div>
      </div>
    );

  return (
    <div className={styles.cxCall}>
      {group("Path values", call.route ?? [], route, setRoute)}
      {group("Filters", call.query, query, setQuery)}
      {group(call.upload ? "Fields sent with the file" : "Details", call.body, body, setBody)}
      {call.upload ? <div className={styles.cxField}><label>File{call.fileField ? <span className={styles.cxType}>{call.fileField}</span> : null}</label><input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></div> : null}
      {unknownBody ? (
        <div className={styles.cxField}>
          <label>Body (JSON, optional)<span className={styles.cxType}>fields not read from the source</span></label>
          <textarea className={`${styles.cxInput} ${styles.cxMono}`} rows={4} value={rawBody} placeholder="{ }" onChange={(e) => setRawBody(e.target.value)} />
        </div>
      ) : null}
      {err ? <div className={styles.cxErr} role="alert">{err}</div> : null}
      <div className={styles.cxRow}>
        <button type="button" className={styles.cxBtnP} disabled={busy} onClick={run}>{busy ? "Working…" : call.download ? "Download" : call.method === "GET" ? "Run" : "Send"}</button>
        <span className={styles.cxMuted}>{call.method} {call.path}{call.permission ? ` · needs ${call.permission}` : ""}</span>
      </div>
      {result ? <ResultView data={result.data} /> : null}
    </div>
  );
};

/* ------------------------------------------------------------------ one record screen */

type Mode = { kind: "none" } | { kind: "new" } | { kind: "record"; rec: ApiRecord };

const EntityScreen: React.FC<{ api: CrmApi; ent: CatalogEntity }> = ({ api, ent }) => {
  const backend = useCrmBackend();
  const [schema, setSchema] = useState<Schema | null>(null);
  const [rows, setRows] = useState<ApiRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [qNow, setQNow] = useState("");
  const [sort, setSort] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "none" });
  const [live, setLive] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true); setErr(null);
    try {
      const r = await api.list(ent.path, { page, pageSize: PAGE_SIZE, q: qNow, sort });
      if (my !== seq.current) return; // a newer request is running
      setRows(r.items ?? []); setTotal(r.total ?? 0);
    } catch (e) { if (my === seq.current) { setErr(errText(e)); setRows([]); setTotal(0); } }
    finally { if (my === seq.current) setLoading(false); }
  }, [api, ent.path, page, qNow, sort]);

  useEffect(() => { void load(); }, [load, backend?.resyncTick]);
  useEffect(() => {
    let live = true;
    setSchema(null);
    api.schema(ent.path).then((s) => { if (live) setSchema(s); }).catch(() => { if (live) setSchema({ input: [], columns: [] }); });
    return () => { live = false; };
  }, [api, ent.path]);
  useEffect(() => { setPage(1); setQ(""); setQNow(""); setSort(""); setMode({ kind: "none" }); setLive(null); }, [ent.key]);

  // live refresh: a change to this entity reloads the page (debounced); the open record is refreshed too
  useEffect(() => {
    if (!backend) return;
    let timer: number | undefined;
    const off = backend.subscribe((c) => {
      if (c.service !== ent.service || c.entity !== ent.entity) return;
      setLive(`${c.action}${c.number ? ` ${c.number}` : ""}${c.by ? ` by ${c.by}` : ""}`);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void load(), 400);
      setMode((m) => m.kind === "record" && m.rec.id === c.id ? (c.action === "Deleted" ? { kind: "none" } : m) : m);
    });
    return () => { off(); window.clearTimeout(timer); };
  }, [backend, ent.service, ent.entity, load]);

  const columns = useMemo(() => {
    const cols = (schema?.columns ?? []).filter((c) => c.type !== "json" && c.name !== "id" && c.name !== "version" && c.name !== "rowVersion");
    const first = cols.filter((c) => c.name === "number" || c.name === "name" || c.name === "title" || c.name === "subject");
    const rest = cols.filter((c) => !first.includes(c));
    return [...first, ...rest].slice(0, 8);
  }, [schema]);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const toggleSort = (name: string) => setSort((s) => (s === name ? `-${name}` : s === `-${name}` ? "" : name));

  const exportCsv = async () => {
    try { await api.download("GET", `${ent.path}/export`, { query: { q: qNow, sort } }, `${ent.key}.csv`); }
    catch (e) { setErr(errText(e)); }
  };

  const getActions = ent.actions.filter((a) => a.method === "GET");
  const otherActions = ent.actions.filter((a) => a.method !== "GET");

  return (
    <div className={styles.cxScreen}>
      <div className={styles.cxHead}>
        <div>
          <h3 className={styles.cxH}>{ent.plural}</h3>
          <div className={styles.cxMuted}>{modName(ent.service)}{ent.readOnly ? " · read only" : ""}</div>
        </div>
        <div className={styles.cxRow}>
          {live ? <span className={styles.cxLive} title="Updated live from another session">● {live}</span> : null}
          <button type="button" className={styles.cxBtn} onClick={() => void load()}>Refresh</button>
          <button type="button" className={styles.cxBtn} onClick={exportCsv}>Export CSV</button>
          {!ent.readOnly ? <button type="button" className={styles.cxBtnP} onClick={() => setMode({ kind: "new" })}>New {ent.label.toLowerCase()}</button> : null}
        </div>
      </div>

      <form className={styles.cxSearch} onSubmit={(e) => { e.preventDefault(); setPage(1); setQNow(q); }}>
        <input className={styles.cxInput} placeholder={`Search ${ent.plural.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className={styles.cxBtn}>Search</button>
        {qNow ? <button type="button" className={styles.cxBtn} onClick={() => { setQ(""); setQNow(""); setPage(1); }}>Clear</button> : null}
      </form>

      {err ? <div className={styles.cxErr} role="alert">{err}</div> : null}

      <div className={styles.cxSplit}>
        <div className={styles.cxList}>
          {loading && rows.length === 0 ? <Skeleton cols={Math.max(3, columns.length || 5)} /> : null}
          <div className={styles.cxScroll} hidden={loading && rows.length === 0}>
            <table className={styles.cxTable}>
              <thead>
                <tr>{columns.map((c) => (
                  <th key={c.name} onClick={() => toggleSort(c.name)} className={styles.cxSortable}>
                    {label(c.name)}{sort === c.name ? " ▲" : sort === `-${c.name}` ? " ▼" : ""}
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={mode.kind === "record" && mode.rec.id === r.id ? styles.cxSel : undefined} onClick={() => setMode({ kind: "record", rec: r })}>
                    {columns.map((c) => <td key={c.name}>{fmt(r[c.name])}</td>)}
                  </tr>
                ))}
                {!loading && rows.length === 0 && !err ? <tr><td colSpan={Math.max(1, columns.length)} className={styles.cxEmpty}>No records.</td></tr> : null}

              </tbody>
            </table>
          </div>
          <div className={styles.cxPager}>
            <button type="button" className={styles.cxBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span>Page {page} of {pages} · {total} record{total === 1 ? "" : "s"}</span>
            <button type="button" className={styles.cxBtn} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>

        {mode.kind !== "none" ? (
          <aside className={styles.cxSide}>
            {mode.kind === "new" ? (
              <RecordForm api={api} ent={ent} schema={schema} onSaved={(r) => { setMode({ kind: "record", rec: r }); void load(); }} onCancel={() => setMode({ kind: "none" })} />
            ) : (
              <RecordPanel api={api} ent={ent} schema={schema} rec={mode.rec} getActions={getActions} otherActions={otherActions}
                onChanged={(r) => { if (r) setMode({ kind: "record", rec: r }); void load(); }}
                onDeleted={() => { setMode({ kind: "none" }); void load(); }}
                onClose={() => setMode({ kind: "none" })} />
            )}
          </aside>
        ) : null}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ create / edit form */

const RecordForm: React.FC<{ api: CrmApi; ent: CatalogEntity; schema: Schema | null; rec?: ApiRecord; onSaved: (r: ApiRecord) => void; onCancel: () => void }> = ({ api, ent, schema, rec, onSaved, onCancel }) => {
  // the service sends the field list at /schema; until it arrives (or if it cannot be read) the list generated from the C# input class is used
  const fields: SchemaField[] = schema?.input && schema.input.length > 0 ? schema.input : (ent.input ?? []);
  const ready = !!schema || fields.length > 0;
  const [vals, setVals] = useState<Record<string, string>>({});
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const v: Record<string, string> = {};
    for (const f of fields) v[f.name] = rec ? toInput(f.type, rec[f.name]) : "";
    setVals(v);
    setRaw(rec ? JSON.stringify(Object.fromEntries(Object.entries(rec).filter(([k]) => !["id", "number", "version", "ownerName"].includes(k))), null, 2) : "{\n  \n}");
  }, [schema, rec]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setErr(null); setBusy(true);
    try {
      const body = fields.length > 0 ? buildBody(fields, vals) : (() => { try { return JSON.parse(raw); } catch { throw new Error("The body is not valid JSON."); } })();
      const saved = rec ? await api.update(ent.path, rec.id, body, rec.version) : await api.create(ent.path, body);
      onSaved(saved);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className={styles.cxHead}><h4 className={styles.cxH}>{rec ? `Edit ${rec.number ?? ent.label.toLowerCase()}` : `New ${ent.label.toLowerCase()}`}</h4></div>
      {!ready ? <Skeleton rows={5} cols={2} /> : null}
      {ready && fields.length === 0 ? (
        <div className={styles.cxField}>
          <label>Record (JSON)<span className={styles.cxType}>this screen publishes no field list</span></label>
          <textarea className={`${styles.cxInput} ${styles.cxMono}`} rows={10} value={raw} onChange={(e) => setRaw(e.target.value)} />
        </div>
      ) : null}
      <div className={styles.cxGrid1}>{fields.map((f) => <FieldInput key={f.name} f={f} value={vals[f.name] ?? ""} onChange={(v) => setVals({ ...vals, [f.name]: v })} />)}</div>
      {err ? <div className={styles.cxErr} role="alert">{err}</div> : null}
      <div className={styles.cxRow}>
        <button type="button" className={styles.cxBtnP} disabled={busy || !ready} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" className={styles.cxBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ record panel: details, edit, actions, delete */

const RecordPanel: React.FC<{
  api: CrmApi; ent: CatalogEntity; schema: Schema | null; rec: ApiRecord; getActions: CatalogCall[]; otherActions: CatalogCall[];
  onChanged: (r?: ApiRecord) => void; onDeleted: () => void; onClose: () => void;
}> = ({ api, ent, schema, rec, getActions, otherActions, onChanged, onDeleted, onClose }) => {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState<CatalogCall | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [full, setFull] = useState<ApiRecord>(rec);

  // fetch the full record (the list row may be trimmed) and keep it fresh
  useEffect(() => {
    let alive = true;
    setFull(rec); setEditing(false); setOpen(null); setConfirm(false); setErr(null);
    api.get(ent.path, rec.id).then((r) => { if (alive) setFull(r); }).catch(() => { /* the list row is still shown */ });
    return () => { alive = false; };
  }, [api, ent.path, rec]);

  const del = async () => {
    setErr(null);
    try { await api.remove(ent.path, rec.id); onDeleted(); } catch (e) { setErr(errText(e)); setConfirm(false); }
  };

  if (editing) {
    return <RecordForm api={api} ent={ent} schema={schema} rec={full} onSaved={(r) => { setEditing(false); onChanged(r); }} onCancel={() => setEditing(false)} />;
  }
  const entries = Object.entries(full).filter(([k]) => k !== "rowVersion");
  return (
    <div>
      <div className={styles.cxHead}>
        <h4 className={styles.cxH}>{full.number ?? ent.label}{full.name ? ` · ${full.name}` : ""}</h4>
        <button type="button" className={styles.cxBtn} onClick={onClose}>Close</button>
      </div>
      <dl className={styles.cxDl}>
        {entries.map(([k, v]) => (<React.Fragment key={k}><dt>{label(k)}</dt><dd>{fmt(v)}</dd></React.Fragment>))}
      </dl>
      {err ? <div className={styles.cxErr} role="alert">{err}</div> : null}
      {!ent.readOnly ? (
        <div className={styles.cxRow}>
          <button type="button" className={styles.cxBtnP} onClick={() => setEditing(true)}>Edit</button>
          {confirm ? (
            <>
              <button type="button" className={styles.cxBtnD} onClick={del}>Yes, delete</button>
              <button type="button" className={styles.cxBtn} onClick={() => setConfirm(false)}>Keep</button>
            </>
          ) : <button type="button" className={styles.cxBtn} onClick={() => setConfirm(true)}>Delete</button>}
        </div>
      ) : null}

      {ent.actions.length > 0 ? (
        <div className={styles.cxGroup}>
          <div className={styles.cxGroupT}>Actions</div>
          <div className={styles.cxChips}>
            {[...otherActions, ...getActions].map((a) => (
              <button key={`${a.method}-${a.path}`} type="button" className={open === a ? styles.cxChipOn : styles.cxChip} onClick={() => setOpen(open === a ? null : a)}>
                {a.label}{a.method === "GET" ? " ↗" : ""}
              </button>
            ))}
          </div>
          {open ? <CallForm key={`${rec.id}-${open.method}-${open.path}`} api={api} call={open} basePath={ent.path} recordId={rec.id} onDone={async () => { try { const r = await api.get(ent.path, rec.id); setFull(r); onChanged(r); } catch { onChanged(); } }} /> : null}
        </div>
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------------ operations (endpoints that are not a record screen) */

const OperationsScreen: React.FC<{ api: CrmApi; service: string }> = ({ api, service }) => {
  const ops = useMemo(() => OPERATIONS.filter((o) => o.service === service), [service]);
  const [sel, setSel] = useState<CatalogCall | null>(null);
  const [filter, setFilter] = useState("");
  useEffect(() => setSel(null), [service]);
  const shown = ops.filter((o) => `${o.label} ${o.path}`.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className={styles.cxScreen}>
      <div className={styles.cxHead}>
        <div><h3 className={styles.cxH}>{modName(service)} operations</h3><div className={styles.cxMuted}>{ops.length} endpoints that are not a record screen (reports, imports, settings, e-sign, jobs…)</div></div>
      </div>
      <input className={styles.cxInput} placeholder="Filter operations" value={filter} onChange={(e) => setFilter(e.target.value)} />
      <div className={styles.cxSplit}>
        <ul className={styles.cxOps}>
          {shown.map((o) => (
            <li key={`${o.method}-${o.path}`}>
              <button type="button" className={sel === o ? styles.cxOpOn : styles.cxOp} onClick={() => setSel(o)}>
                <span className={styles.cxMeth}>{o.method}</span> <span>{o.label}</span>
                <span className={styles.cxMuted}>{o.path.replace(/^api\/[^/]+\//, "")}</span>
              </button>
            </li>
          ))}
          {shown.length === 0 ? <li className={styles.cxEmpty}>No operations.</li> : null}
        </ul>
        {sel ? <aside className={styles.cxSide}><div className={styles.cxHead}><h4 className={styles.cxH}>{sel.label}</h4></div><CallForm key={`${sel.method}-${sel.path}`} api={api} call={sel} basePath="" /></aside> : null}
      </div>
    </div>
  );
};

/** One server screen on its own (no module list): used by the left-hand tabs whose records live only in the CRM services. */
export function CrmEntityView({ entityKey }: { entityKey: string }): React.ReactElement {
  const backend = useCrmBackend();
  const ent = ENTITIES.find((e) => e.key === entityKey);
  if (!backend) return <div className={styles.cxEmpty}>The CRM backend is not connected.</div>;
  if (backend.unauthorized) return <div className={styles.cxErr}>The server did not accept the token. Sign in again.</div>;
  if (!ent) return <div className={styles.cxEmpty}>This screen is not available in the CRM catalog.</div>;
  return <EntityScreen key={ent.key} api={backend.api} ent={ent} />;
};

/* ------------------------------------------------------------------ the page */

type Pick = { kind: "entity"; key: string } | { kind: "ops"; service: string };

const CrmExplorer: React.FC<{ initialKey?: string }> = ({ initialKey }) => {
  const backend = useCrmBackend();
  const [pick, setPick] = useState<Pick>({ kind: "entity", key: initialKey && ENTITIES.some((e) => e.key === initialKey) ? initialKey : ENTITIES[0]?.key ?? "" });
  useEffect(() => { if (initialKey && ENTITIES.some((e) => e.key === initialKey)) setPick({ kind: "entity", key: initialKey }); }, [initialKey]);
  const [filter, setFilter] = useState("");

  const groups = useMemo(() => {
    const m = new Map<string, CatalogEntity[]>();
    for (const e of ENTITIES) {
      if (filter && !`${e.plural} ${e.label} ${e.path}`.toLowerCase().includes(filter.toLowerCase())) continue;
      m.set(e.service, [...(m.get(e.service) ?? []), e]);
    }
    for (const o of OPERATIONS) if (!m.has(o.service) && (!filter || modName(o.service).toLowerCase().includes(filter.toLowerCase()))) m.set(o.service, []);
    return Array.from(m.entries());
  }, [filter]);
  const opCount = useMemo(() => {
    const c: Record<string, number> = {};
    for (const o of OPERATIONS) c[o.service] = (c[o.service] ?? 0) + 1;
    return c;
  }, []);

  if (!backend) return <div className={styles.cxEmpty}>The CRM backend is not connected. Wrap the app in CrmBackendProvider.</div>;
  const ent = pick.kind === "entity" ? ENTITIES.find((e) => e.key === pick.key) : undefined;
  const stateText = { off: "Not signed in", connecting: "Connecting…", connected: "Live", reconnecting: "Reconnecting…", disconnected: "Offline" }[backend.state];

  return (
    <div className={styles.cxWrap}>
      <nav className={styles.cxNav} aria-label="CRM modules">
        <div className={styles.cxState} data-state={backend.state}>● {stateText}</div>
        {backend.unauthorized ? <div className={styles.cxErr}>The server did not accept the token. Sign in again.</div> : null}
        <input className={styles.cxInput} placeholder="Find a screen" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {groups.map(([service, list]) => (
          <div key={service} className={styles.cxMod}>
            <div className={styles.cxModT}>{modName(service)}</div>
            {list.map((e) => (
              <button key={e.key} type="button" className={pick.kind === "entity" && pick.key === e.key ? styles.cxNavOn : styles.cxNavItem} onClick={() => setPick({ kind: "entity", key: e.key })}>{e.plural}</button>
            ))}
            {opCount[service] ? (
              <button type="button" className={pick.kind === "ops" && pick.service === service ? styles.cxNavOn : styles.cxNavItem} onClick={() => setPick({ kind: "ops", service })}>
                Operations ({opCount[service]})
              </button>
            ) : null}
          </div>
        ))}
        {groups.length === 0 ? <div className={styles.cxMuted}>No match.</div> : null}
      </nav>
      <main className={styles.cxMain}>
        {ent ? <EntityScreen key={ent.key} api={backend.api} ent={ent} /> : null}
        {pick.kind === "ops" ? <OperationsScreen api={backend.api} service={pick.service} /> : null}
      </main>
    </div>
  );
};

export default CrmExplorer;